
import React from 'react';
import { OrderWithCustomer } from '../types/orderTypes';
import { useOrderEdit } from '../hooks/useOrderEdit';
import OrderFormCustomerSection from './OrderFormCustomerSection';
import OrderFormDetailsSection from './OrderFormDetailsSection';
import OrderFormProductsSection from './OrderFormProductsSection';
import OrderFormActions from './OrderFormActions';
import { Container } from '@/components/ui/container';
import { Card } from '@/components/ui/card';
import { Loader2, ChevronRight } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";

interface OrderEditFormProps {
  orderId: string;
  handleOrderUpdated: (updatedOrder: OrderWithCustomer) => void;
  handleBackToOrders: () => void;
  organizationId?: string | null;
}

const OrderEditForm = ({ orderId, handleOrderUpdated, handleBackToOrders, organizationId }: OrderEditFormProps) => {
  const {
    customerId,
    customerName,
    selectedCustomer,
    customerPhone,
    customerEmail,
    customerCountry,
    customerRegion,
    customerMunicipality,
    orderNumber,
    orderDate,
    shippingAddress,
    notes,
    products,
    totalAmount,
    isSubmitting,
    isLoading,
    handleSelectCustomer,
    handleAddProduct,
    handleRemoveProduct,
    handleSubmit,
    setOrderNumber,
    setOrderDate,
    setShippingAddress,
    setNotes,
    setCustomerCountry,
    setCustomerRegion,
    setCustomerMunicipality
  } = useOrderEdit({ orderId, handleOrderUpdated, handleBackToOrders, organizationId });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Container className="py-6">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink onClick={handleBackToOrders} className="text-primary cursor-pointer">
              Commandes
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <span className="text-muted-foreground">Modifier la commande #{orderNumber}</span>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Modifier la commande #{orderNumber}</h1>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <OrderFormCustomerSection
            orderNumber={orderNumber}
            orderDate={orderDate}
            handleOrderNumberChange={setOrderNumber}
            handleOrderDateChange={setOrderDate}
            handleSelectCustomer={handleSelectCustomer}
            selectedCustomerId={customerId}
            selectedCustomerName={customerName !== 'Client par défaut' ? customerName : undefined}
            selectedCustomer={selectedCustomer}
          />
          
          <OrderFormDetailsSection
            shippingAddress={shippingAddress}
            notes={notes}
            handleShippingAddressChange={setShippingAddress}
            handleNotesChange={setNotes}
            customerCountry={customerCountry}
            customerRegion={customerRegion}
            customerMunicipality={customerMunicipality}
            customerEmail={customerEmail}
            customerPhone={customerPhone}
            setCustomerCountry={setCustomerCountry}
            setCustomerRegion={setCustomerRegion}
            setCustomerMunicipality={setCustomerMunicipality}
          />
          
          <OrderFormProductsSection
            products={products}
            totalAmount={totalAmount}
            handleAddProduct={handleAddProduct}
            handleRemoveProduct={handleRemoveProduct}
          />
        </div>
        
        <Card className="bg-white p-4 flex justify-between items-center border-0 shadow-sm">
          <div className="font-semibold text-xl">
            Total: <span className="text-indigo-700">{totalAmount.toFixed(2)} DA</span>
          </div>
          
          <OrderFormActions
            handleBackToOrders={handleBackToOrders}
            isSubmitting={isSubmitting}
            isEditing={true}
            isValid={!!customerPhone && products.length > 0}
          />
        </Card>
      </form>
    </Container>
  );
};

export default OrderEditForm;
